'use client';

import { useState, useEffect } from 'react';
import { TeamLeaguePlayerInfo } from '@/entities';
import { isValidScore } from '@/infrastructure/utils/tieBreakerHelpers';

import { ActionButtons } from '@/components/molecules/ActionButtons';

export interface PlayedMatch {
	uuid: string;
	left: TeamLeaguePlayerInfo;
	right: TeamLeaguePlayerInfo;
	leftScore: number;
	rightScore: number;
	isMatchOverAndCompleted: boolean;
}

interface PlayedMatchesListBaseProps {
	playedMatches: PlayedMatch[];
	isMatchOverAndCompleted: boolean;
	editScores: boolean;
	setEditScores: React.Dispatch<React.SetStateAction<boolean>>;
	updatePlayedTieBreakerSequences: (updated: PlayedMatch[]) => void;
	isEditing: boolean;
	isMobile?: boolean;
	isUpdatingSequence: boolean;
	isPublicMatchResultPage?: boolean;
}

const mapMatchesToLocal = (matches: PlayedMatch[]) =>
	matches.map((match) => ({
		uuid: match.uuid,
		left: match.left,
		right: match.right,
		isMatchOverAndCompleted: match.isMatchOverAndCompleted,
		leftScore: String(match.leftScore),
		rightScore: String(match.rightScore)
	}));

export const PlayedMatchesListBase = ({
	playedMatches,
	isMatchOverAndCompleted,
	editScores,
	setEditScores,
	updatePlayedTieBreakerSequences,
	isEditing,
	isMobile = false,
	isUpdatingSequence,
	isPublicMatchResultPage = false
}: PlayedMatchesListBaseProps) => {
	const [localMatches, setLocalMatches] = useState(mapMatchesToLocal(playedMatches));

	useEffect(() => {
		setLocalMatches(mapMatchesToLocal(playedMatches));
	}, [playedMatches]);

	const handleScoreChange = (index: number, side: 'left' | 'right', value: string) => {
		if (!isValidScore(value)) return;

		const updated = [...localMatches];
		const item = updated[index];
		if (!item) return;

		if (side === 'left') {
			item.leftScore = value;
		} else {
			item.rightScore = value;
		}
		setLocalMatches(updated);
	};

	if (!playedMatches.length) return null;

	const handleSave = () => {
		const parsedMatches: PlayedMatch[] = localMatches.map((m) => ({
			uuid: m.uuid,
			left: m.left,
			right: m.right,
			isMatchOverAndCompleted: m.isMatchOverAndCompleted,
			leftScore: Number(m.leftScore),
			rightScore: Number(m.rightScore)
		}));

		updatePlayedTieBreakerSequences(parsedMatches);
		setEditScores(false);
	};

	const handleCancel = () => {
		setEditScores(false);
	};

	return (
		<div className="mt-6 p-6 border rounded-xl border-gray-200">
			{!isMatchOverAndCompleted && (
				<div className="text-sm flex items-center gap-4 pb-4">
					<p className="font-semibold text-sm text-left text-gray-600">
						{isMatchOverAndCompleted ? 'Rotations played:' : 'Already played:'}
					</p>
					{!editScores && !isPublicMatchResultPage && (
						<button
							type="button"
							onClick={() => setEditScores(true)}
							disabled={isUpdatingSequence}
							className="cursor-pointer bg-darkorange-400 text-white font-semibold py-1 px-2 rounded-md text-xs"
						>
							Edit
						</button>
					)}
				</div>
			)}

			{localMatches.map((match, index) => {
				if (isMobile) {
					return (
						<div key={`played_matches_${index}`} className="flex flex-col border-b border-darkorange-400 last:border-none py-2 gap-2">
							<div className="flex justify-between items-center text-sm text-gray-800">
								<div className="flex items-center gap-2 truncate">
									<div className="flex flex-col">
										<span>{match.left.fullName}</span>
										{match.left.secondPlayerName && <span>{match.left.secondPlayerName}</span>}
									</div>
								</div>
								<ScoreInput
									value={match.leftScore}
									onChange={(val) => handleScoreChange(index, 'left', val)}
									isEditing={isEditing}
									isWinner={isMatchOverAndCompleted && Number(match.leftScore) > Number(match.rightScore)}
								/>
							</div>

							<div className="flex justify-between items-center text-sm text-gray-800 mt-1">
								<div className="flex items-center gap-2 truncate">
									<div className="flex flex-col">
										<span>{match.right.fullName}</span>
										{match.right.secondPlayerName && <span>{match.right.secondPlayerName}</span>}
									</div>
								</div>
								<ScoreInput
									value={match.rightScore}
									onChange={(val) => handleScoreChange(index, 'right', val)}
									isEditing={isEditing}
									isWinner={isMatchOverAndCompleted && Number(match.rightScore) > Number(match.leftScore)}
								/>
							</div>
						</div>
					);
				}

				return (
					<div
						key={`played_matches_${index}`}
						className="flex flex-col sm:grid sm:grid-cols-[200px_auto_200px] items-center gap-2 py-4 border-b border-gray-200 last:border-none"
					>
						<div className="flex items-center gap-2 text-gray-800">
							<div className="flex flex-col truncate text-sm leading-tight">
								<span>{match.left.fullName}</span>
								{match.left.secondPlayerName && <span>{match.left.secondPlayerName}</span>}
							</div>
						</div>

						<div className="flex justify-center gap-2">
							<ScoreInput
								value={match.leftScore}
								onChange={(val) => handleScoreChange(index, 'left', val)}
								isEditing={isEditing}
								isWinner={isMatchOverAndCompleted && Number(match.leftScore) > Number(match.rightScore)}
							/>
							<ScoreInput
								value={match.rightScore}
								onChange={(val) => handleScoreChange(index, 'right', val)}
								isEditing={isEditing}
								isWinner={isMatchOverAndCompleted && Number(match.rightScore) > Number(match.leftScore)}
							/>
						</div>

						<div className="flex items-center gap-2 justify-end text-gray-800 w-full sm:justify-end sm:flex-row-reverse">
							<div className="flex flex-col truncate text-right text-sm leading-tight">
								<span>{match.right.fullName}</span>
								{match.right.secondPlayerName && <span>{match.right.secondPlayerName}</span>}
							</div>
						</div>
					</div>
				);
			})}

			{editScores && (
				<ActionButtons
					className="w-full justify-center pt-8"
					submitButtonClassName="w-fit !bg-darkorange-400 !text-darkorange-400 !text-white disabled:!text-darkorange-200 !gap-3"
					closeButtonClassName="!w-fit !text-darkorange-600 w-full"
					onClose={handleCancel}
					onSubmit={handleSave}
					submitButtonText="Save"
					closeButtonText="Cancel"
					iconClassName="size-4"
					submitButtonDisabled={isUpdatingSequence}
				/>
			)}
		</div>
	);
};

interface ScoreInputProps {
	value: string;
	onChange: (val: string) => void;
	isEditing: boolean;
	isWinner: boolean;
}

export const ScoreInput = ({ value, onChange, isEditing, isWinner }: ScoreInputProps) => {
	return isEditing ? (
		<input
			type="text"
			inputMode="numeric"
			value={value}
			onChange={(e) => onChange(e.target.value)}
			className="custom-number-input w-12 text-center rounded-lg px-2 py-1 border text-sm font-medium bg-white border-gray-400 text-gray-800 focus:outline-none focus:ring-0"
		/>
	) : (
		<span
			className={`w-9 h-[36px] justify-center flex items-center rounded-lg px-3 py-1 text-sm font-medium ${
				isWinner ? 'bg-green-600 text-white' : 'bg-orange-100 text-gray-800'
			}`}
		>
			{value}
		</span>
	);
};
